'use client';
import { ChevronRight } from 'lucide-react';
import { useTheme } from '@/lib/ThemeContext';
import { resolveServiceIcon } from '@/lib/serviceIcons';
import { AppLink } from '@/lib/appLink';

const DEFAULT_ITEMS = [
  {
    icon: 'home',
    title: 'Будівництво будинків',
    image_url: 'https://media.base44.com/images/public/6a1311bd1062b12420e3c449/24f055dbd_1779640019874869.jpg',
    description:
      '<ul><li>Будівництво будинків (фундамент, коробка, дах, фасад)</li><li>Будівництво приватних будинків з гарантією якості</li><li>Покрівельні роботи будь-якої складності</li><li>Монтаж та ремонт дахів</li></ul>',
    link: '/budivnytstvo',
  },
  {
    icon: 'building2',
    title: 'Фасадні роботи',
    image_url: 'https://media.base44.com/images/public/6a1311bd1062b12420e3c449/023cb03f4_1779640493773383.jpg',
    description:
      '<ul><li>Утеплення фасадів (пінопласт, мінеральна вата, PIR-плити)</li><li>Декоративна штукатурка (короїд, барашок)</li><li>Клінкерна плитка та натуральний камінь</li><li>Монтаж водостоків та підшивка софітів</li></ul>',
    link: '/fasad',
  },
  {
    icon: 'hammer',
    title: 'Ремонт та реконструкція',
    image_url: 'https://media.base44.com/images/public/6a1311bd1062b12420e3c449/9ece18ba7_1779639888200139.jpg',
    description:
      '<ul><li>Капітальний ремонт приміщень</li><li>Реконструкція будинків</li><li>Внутрішні ремонтні роботи</li><li>Чистові роботи</li></ul>',
    link: '/remont',
  },
];

const DESC_CLASS =
  '[&_ul]:space-y-3 [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0 [&_li]:relative [&_li]:pl-5 [&_li]:text-sm [&_li]:text-muted-foreground [&_li]:before:content-[\'\'] [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-[0.45rem] [&_li]:before:w-1.5 [&_li]:before:h-1.5 [&_li]:before:bg-gold [&_li]:before:rounded-full [&_b]:font-semibold [&_i]:italic';

/**
 * @param {import('@/types/cms').ServicesSectionPageSection} [section]
 */
export default function ServicesSection({ section = {} }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const subTitle = section.sub_title || 'Що ми робимо';
  const title = section.title || 'Перелік послуг';
  const items = section.items?.length ? section.items : DEFAULT_ITEMS;

  return (
    <section className="bg-background py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <span className="text-gold text-xs font-semibold uppercase tracking-widest">{subTitle}</span>
          <h2 className="text-3xl sm:text-4xl font-black text-foreground mt-3 mb-4">{title}</h2>
          <div className="gold-divider" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {items.map((item) => {
            const Icon = resolveServiceIcon(item.icon);
            const imageUrl = item.image_url || item.imageUrl || '';
            const link = item.link || '/';
            const description = item.description || '';

            return (
              <div
                key={`${item.title}-${link}`}
                className="bg-card border border-border rounded-xl overflow-hidden group hover:border-gold/50 transition-all duration-300"
              >
                {imageUrl && (
                  <div className="relative h-44 overflow-hidden border-b border-border">
                    <img
                      src={imageUrl}
                      alt={item.title}
                      className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/90 via-card/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end gap-3">
                      <div className="w-10 h-10 bg-gold/20 border border-gold/40 rounded-lg flex items-center justify-center backdrop-blur-sm shrink-0">
                        <Icon className="w-5 h-5 text-gold" />
                      </div>
                      <h3 className={`text-lg font-bold drop-shadow-lg ${isLight ? 'text-foreground' : 'text-white'}`}>
                        {item.title}
                      </h3>
                    </div>
                  </div>
                )}

                <div className="p-6">
                  {description ? (
                    <div className={DESC_CLASS} dangerouslySetInnerHTML={{ __html: description }} />
                  ) : null}
                  <AppLink
                    to={link}
                    className="mt-6 flex items-center gap-2 text-gold text-sm font-semibold hover:gap-3 transition-all duration-300"
                  >
                    Детальніше <ChevronRight className="w-4 h-4" />
                  </AppLink>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
